import type { Fix } from "../api";

/**
 * FixSummary is one correction on a line: the word, and what it becomes.
 *
 * It exists because both places that show a correction — the chapter's list and
 * the correction sheet — have to get the same two bidi details right, and
 * neither is visible to somebody reading Latin text.
 *
 * **The arrow points the way the line reads.** Between two Persian words the
 * line is laid out right to left, so the word being corrected is on the right
 * and its replacement on the left. A `→` there points from the new spelling
 * back at the old one, which is the sentence backwards. Unicode does not mirror
 * U+2192 in a right-to-left run — it mirrors brackets, not arrows — so `:dir()`
 * does it, resolved by the browser from the same `dir="auto"` that laid the
 * words out. A class on an ancestor cannot: the direction here comes from the
 * *content*, and is not known until it is rendered.
 *
 * **The hint is isolated.** A Finglish hint or an IPA reading is a Latin run
 * inside a Persian line, and its quotes and slashes are neutral characters that
 * take the direction of whatever surrounds them: “Ka-taa-yoon” came out with
 * its quotes swapped, and /bɒː deɣˈɣæt/ is worse. `<bdi>` makes each an atomic
 * run with its own direction.
 *
 * What must NOT be isolated is `from_text`. The element above this one carries
 * `dir="auto"`, which skips any descendant with its own direction when it looks
 * for the first strong character — and `<bdi>` has one implicitly. Wrapping the
 * word too would leave that element reading an arrow and a dash, finding
 * nothing strong, and laying the whole row out left to right.
 */
export default function FixSummary({ fix }: { fix: Fix }) {
  return (
    <>
      <span className="font-medium">{fix.from_text}</span>
      {fix.kind === "replace" ? (
        <>
          {" "}
          <span className="bidi-arrow">→</span> <bdi>{fix.to_text}</bdi>
        </>
      ) : (
        <>
          {" — "}
          <bdi>“{fix.hint}”</bdi>
        </>
      )}
    </>
  );
}
